// Surveys 2.0 runtime state (spec §6). One survey at a time.
//
// Flow: CommunicationManager `start-survey` → startSurveyV2() routes to the
// `survey` page (skeleton) → load() asks GET /surveys/:id. 200 → v2 renderer;
// 404 / error → the caller falls back to the legacy FeedbackFlow untouched.
//
// Navigation model: `path` is the list of blocks shown, `cursor` the one on
// screen (full screen up/down can move it back). Answering at the cursor
// truncates everything after it (a changed answer may change the route) and
// drops answers of blocks that left the path, then appends the next block or
// finishes on an ending. Every answer PUTs the full state (surveyResponseSync).
//
// "Skip when known" (Helper/surveyKnownContact.ts): contact questions whose
// value an identified contact already has (`known`, fixed per response) never
// enter `path`; navigation passes them like answered ones, and only the save
// payload puts them back on the path and lists them in `skippedKnown`. Their
// values are never sent: the server answers them from the session.
import { create } from "zustand";
import CommunicationManager from "../Helper/CommunicationManager";
import Communicator from "../Helper/Communicator";
import { ensureRecaptchaLoaded, getRecaptchaToken } from "../Helper/Recaptcha";
import {
  isEndingId,
  nextBlockId,
  progressEstimate,
} from "../Helper/surveyLogic";
import {
  clearSurveyState,
  fetchCurrentSurveyResponse,
  fetchSurveyDefinition,
  generateResponseId,
  hasStoredSurveyState,
  postSurveyImpression,
  readSurveyState,
  SurveyResponsePayload,
  SurveyResponseSync,
  SurveySaveState,
  surveyStorageAvailable,
  writeSurveyState,
} from "../Helper/surveyResponseSync";
import { contactFromSession } from "../Helper/surveyText";
import {
  knownContactAnswers,
  NextOf,
  passSkipped,
  shownSteps,
  withSkippedSteps,
} from "../Helper/surveyKnownContact";
import { legacyFormDataOf } from "../Helper/surveyLegacyFormData";
import { surveyAnalytics } from "../Helper/surveyAnalytics";
import type {
  SurveyAnswers,
  SurveyAnswerValue,
  SurveyBlock,
  SurveyChannel,
  SurveyDefinition,
  SurveyEnding,
  SurveyPayload,
  SurveyResumeData,
  SurveyStartOptions,
} from "../Helper/surveyTypes";
import { useConfigStore } from "./Config";
import { useRouterStore } from "./Router";
import { useFormStore, validateEmail as validateEmailAddress } from "./Form";
import { useSessionStore } from "./Session";

export type SurveyRenderFormat = "card" | "full" | "page";

export type SurveyErrorCode =
  | "required"
  | "email"
  | "number"
  | "minSelect"
  | "maxSelect"
  | "other"
  | "date"
  | "consent";

interface SurveyV2State {
  phase: "idle" | "loading" | "ready";
  options?: SurveyStartOptions;
  surveyId?: string;
  payload?: SurveyPayload;
  def?: SurveyDefinition;
  format: SurveyRenderFormat;
  lang: string;
  channel: SurveyChannel;
  responseId: string;
  /** Version the response belongs to (kept when resuming across a publish). */
  responseVersion: number;
  path: string[];
  cursor: number;
  answers: SurveyAnswers;
  drafts: Record<string, SurveyAnswerValue | undefined>;
  errors: Record<string, SurveyErrorCode | undefined>;
  /** Shuffled choice order per block (randomize), fixed for the response. */
  choiceOrder: Record<string, string[]>;
  endingId?: string;
  direction: "forward" | "back";
  saveState: SurveySaveState;
  answeredOnce: boolean;
  resumed: boolean;
  closed: boolean;
  /** The welcome screen is on screen (before the first question). */
  welcome: boolean;
  /** Block id → the contact's value, for the contact questions skipped as known. */
  known: Record<string, string>;

  load: (options: SurveyStartOptions) => Promise<"v2" | "legacy">;
  /** Present a payload directly (mock harness / preview). */
  present: (options: SurveyStartOptions, payload: SurveyPayload) => void;
  /** Start on the welcome screen: on to the first question. */
  startSurvey: () => void;
  setDraft: (blockId: string, value: SurveyAnswerValue | undefined) => void;
  submitCurrent: () => boolean;
  submit: (blockId: string, value: SurveyAnswerValue | undefined) => void;
  skipCurrent: () => void;
  back: () => void;
  forward: () => void;
  close: () => void;
  reset: () => void;
  currentBlock: () => SurveyBlock | undefined;
  currentEnding: () => SurveyEnding | undefined;
  progress: () => number;
  questionNumber: (blockId: string) => number;
  /** The skipped-as-known answers by key (tokens, host callbacks; never saved by the widget). */
  knownAnswers: () => SurveyAnswers;
}

let sync: SurveyResponseSync | null = null;
let lifecycleBound = false;
let loadToken = 0;

const send = (name: string, data?: any) => {
  try {
    CommunicationManager.getInstance().sendMessage({ name, data });
  } catch (exp) {}
};

// Native SDKs before the Surveys 2.0 callbacks (they don't announce
// `surveyCallbacks` in config-update) fire feedbackSent / outboundSent and
// track `outbound-<id>-submitted` only after their own send-feedback POST
// succeeds. They get that legacy message once per completion, marked by its
// spam token (forwarded by every SDK since 7.0) so the server answers 201
// without storing anything (Server surveyResponseAck.ts; the answers are
// already saved). Type and excludeData are a legacy survey's, so the SDK
// sends, keeps and reports what it did for a legacy survey.
const SURVEY_RESPONSE_ACK_TOKEN_PREFIX = "survey-response:";
const LEGACY_SURVEY_EXCLUDE_DATA = {
  customData: false,
  metaData: false,
  consoleLog: true,
  networkLogs: true,
  customEventLog: true,
  screenshot: true,
  replays: true,
};
let legacyAckUntil = 0;

/**
 * True once for the SDK's feedback-sent / feedback-sending-failed reply to that
 * legacy message (the legacy form must not act on it), within a minute and
 * while no legacy form is sending.
 */
export const consumeLegacySurveyAck = () => {
  if (Date.now() > legacyAckUntil || useFormStore.getState().sendingForm) {
    return false;
  }
  legacyAckUntil = 0;
  return true;
};

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/** Validation of a draft before it becomes an answer. */
export const validateSurveyAnswer = (
  block: SurveyBlock,
  value: SurveyAnswerValue | undefined,
): SurveyErrorCode | undefined => {
  if (block.type === "statement") return undefined;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const other = (value as any).other;
    if (typeof other !== "string" || other.trim() === "") {
      const picked = (value as any).choices;
      if (!(Array.isArray(picked) && picked.length > 0) || block.required) {
        return "other";
      }
    }
  }
  if (block.type === "consent") {
    return block.required && value !== true ? "consent" : undefined;
  }
  if (isEmptyValue(value)) {
    return block.required ? "required" : undefined;
  }
  if (block.type === "email" && !validateEmailAddress(String(value).trim())) {
    return "email";
  }
  if (block.type === "number" && isNaN(Number(String(value).replace(",", ".")))) {
    return "number";
  }
  if (block.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return "date";
  }
  if (block.type === "multi") {
    const count = Array.isArray(value)
      ? value.length
      : ((value as any)?.choices?.length ?? 0) + ((value as any)?.other ? 1 : 0);
    if (block.minSelect && count < block.minSelect) return "minSelect";
    if (block.maxSelect && count > block.maxSelect) return "maxSelect";
  }
  return undefined;
};

/** Normalise a valid draft into the stored answer shape. */
const toAnswer = (
  block: SurveyBlock,
  value: SurveyAnswerValue | undefined,
): SurveyAnswerValue | undefined => {
  if (isEmptyValue(value)) return undefined;
  switch (block.type) {
    case "number": {
      const n = Number(String(value).replace(",", "."));
      return isNaN(n) ? undefined : n;
    }
    case "short":
    case "long":
    case "email":
      return String(value).trim();
    default:
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const other = String((value as any).other ?? "").trim();
        const choices = (value as any).choices;
        if (Array.isArray(choices) && choices.length > 0) {
          return other ? { choices, other } : choices;
        }
        return other ? { other } : undefined;
      }
      return value;
  }
};

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const detectChannel = (format: SurveyRenderFormat): SurveyChannel => {
  if (format === "page") return "page";
  if (useConfigStore.getState().isApp) {
    return /android/i.test(navigator.userAgent) ? "android" : "ios";
  }
  return "web";
};

const hostPageUrl = (options?: SurveyStartOptions): string | undefined => {
  const fromHost = (options as any)?.pageUrl;
  if (typeof fromHost === "string" && fromHost) return fromHost;
  try {
    const href = window.top?.location?.href;
    if (href && !href.startsWith("about:")) return href;
  } catch (exp) {}
  try {
    if (document.referrer) return document.referrer;
  } catch (exp) {}
  return undefined;
};

const waitForSession = async (timeoutMs = 4000) => {
  const start = Date.now();
  while (!useSessionStore.getState().sdkKey && Date.now() - start < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 60));
  }
};

/** Parse `fields.__prefill` (`key:value`) into an answer for that block. */
const parsePrefill = (
  def: SurveyDefinition,
  prefill: string | undefined,
): { block: SurveyBlock; value: NonNullable<SurveyBlock["defaultValue"]> } | null => {
  if (!prefill || typeof prefill !== "string") return null;
  const index = prefill.indexOf(":");
  if (index <= 0) return null;
  const key = prefill.slice(0, index).trim();
  const raw = decodeURIComponent(prefill.slice(index + 1)).trim();
  const block = def.blocks.find((b) => b.key === key);
  if (!block || raw === "") return null;
  switch (block.type) {
    case "nps":
    case "csat":
    case "rating":
    case "scale":
    case "number": {
      const n = Number(raw);
      return isNaN(n) ? null : { block, value: n };
    }
    case "yesno": {
      const lower = raw.toLowerCase();
      if (["yes", "true", "1", "y"].includes(lower)) return { block, value: true };
      if (["no", "false", "0", "n"].includes(lower)) return { block, value: false };
      return null;
    }
    case "single": {
      const choice = block.choices?.find(
        (c) =>
          c.id === raw ||
          Object.values(c.label ?? {}).some(
            (l) => String(l).toLowerCase() === raw.toLowerCase(),
          ),
      );
      return choice ? { block, value: choice.id } : null;
    }
    case "multi": {
      const ids = raw
        .split(",")
        .map((part) => part.trim())
        .filter((id) => block.choices?.some((c) => c.id === id));
      return ids.length ? { block, value: ids } : null;
    }
    case "short":
    case "long":
    case "email":
    case "date":
      return { block, value: raw };
    default:
      return null;
  }
};

/**
 * Rebuild path/answers from a resume reminder. Answers whose keys no longer
 * exist (definition changed since) are dropped. The respondent continues at
 * the block after `lastBlockId` per the logic, or at `lastBlockId` itself
 * when it is unanswered (or its successor would end the survey).
 */
export const restoreFromResumeData = (
  def: SurveyDefinition,
  data: SurveyResumeData,
  next: (block: SurveyBlock, answer: SurveyAnswerValue | undefined, answers: SurveyAnswers) => string,
): { path: string[]; answers: SurveyAnswers } | null => {
  if (!def.blocks.length) return null;
  const keys = new Set(def.blocks.map((b) => b.key));
  const answers: SurveyAnswers = {};
  for (const [key, value] of Object.entries(data.answers ?? {})) {
    if (keys.has(key) && value !== undefined && value !== null) answers[key] = value;
  }
  const path = (data.path ?? []).filter((id) => def.blocks.some((b) => b.id === id));
  const lastId =
    data.lastBlockId && def.blocks.some((b) => b.id === data.lastBlockId)
      ? data.lastBlockId
      : path[path.length - 1] ?? def.blocks[0].id;
  const trimmed = path.includes(lastId) ? path.slice(0, path.indexOf(lastId) + 1) : [...path, lastId];
  const last = def.blocks.find((b) => b.id === lastId)!;
  const answered = answers[last.key] !== undefined || last.type === "statement";
  if (answered) {
    const following = next(last, answers[last.key], answers);
    if (following && def.blocks.some((b) => b.id === following)) {
      return { path: [...trimmed, following], answers };
    }
  }
  return { path: trimmed, answers };
};

const bindLifecycle = () => {
  if (lifecycleBound) return;
  lifecycleBound = true;
  const flush = () => {
    try {
      if (sync?.hasUnsaved) sync.flushWithKeepalive();
    } catch (exp) {}
  };
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
};

const initialState = {
  phase: "idle" as const,
  options: undefined,
  surveyId: undefined,
  payload: undefined,
  def: undefined,
  format: "card" as SurveyRenderFormat,
  lang: "en",
  channel: "web" as SurveyChannel,
  responseId: "",
  responseVersion: 0,
  path: [] as string[],
  cursor: 0,
  answers: {} as SurveyAnswers,
  drafts: {},
  errors: {},
  choiceOrder: {},
  endingId: undefined,
  direction: "forward" as const,
  saveState: "idle" as SurveySaveState,
  answeredOnce: false,
  resumed: false,
  closed: false,
  welcome: false,
  known: {} as Record<string, string>,
};

export const useSurveyV2Store = create<SurveyV2State>()((set, get) => {
  const buildPayload = (complete: boolean): SurveyResponsePayload => {
    const s = get();
    const visible = s.path.slice(0, s.cursor + 1);
    const blocksOnly = visible.filter((id) => !isEndingId(s.def!, id));
    const options = s.options;
    const fields = { ...(options?.fields ?? {}) };
    delete (fields as any).__prefill;
    // Skipped-as-known questions go back on the path where they were passed;
    // their values stay out of `answers` (the server reads them from the session).
    const steps = complete && s.endingId ? [...blocksOnly, s.endingId] : blocksOnly;
    const { path, skipped } = withSkippedSteps(s.def!, steps, s.known, s.answers, nextOf());
    const answers: SurveyAnswers = { ...s.answers };
    for (const block of s.def!.blocks) {
      if (s.known[block.id] !== undefined) delete answers[block.key];
    }
    return {
      version: s.responseVersion || s.payload!.version,
      answers,
      path,
      ...(skipped.length > 0 ? { skippedKnown: skipped } : {}),
      lastBlockId: blocksOnly[blocksOnly.length - 1],
      ...(complete ? { status: "completed" as const, endingId: s.endingId } : {}),
      channel: s.channel,
      ...surveyAnalytics.responseExtras(),
      ...(options?.source ? { source: { ...surveyAnalytics.responseExtras().source, ...options.source } } : {}),
      ...(Object.keys(fields).length ? { fields } : {}),
      pageUrl: hostPageUrl(options),
      language: s.lang,
      ...(options?.personalToken ? { personalToken: options.personalToken } : {}),
    };
  };

  const persist = (complete: boolean) => {
    const s = get();
    if (!s.def || !s.surveyId || !s.payload) return;
    const payload = buildPayload(complete);
    sync?.save(payload);
    if (complete) {
      clearSurveyState(s.surveyId);
    } else {
      writeSurveyState(s.surveyId, {
        responseId: s.responseId,
        version: s.payload.version,
        responseVersion: s.responseVersion,
        answers: s.answers,
        path: s.path.slice(0, s.cursor + 1),
        lastBlockId: s.path[s.cursor],
        started: true,
        updatedAt: Date.now(),
      });
    }
  };

  const logicContext = () => {
    const s = get();
    return {
      answers: s.answers,
      contact: contactFromSession(useSessionStore.getState().session),
      fields: s.options?.fields ?? {},
      url: hostPageUrl(s.options),
    };
  };

  /** Where a block's logic goes with `answer` (skipped questions pass with their known value). */
  const nextOf = (): NextOf => {
    const ctx = logicContext();
    return (block, answer) => nextBlockId(get().def!, block, answer, ctx);
  };

  /**
   * Every question is skipped as known: nothing is asked and nothing is saved.
   * In-app the survey closes without showing; a survey page or a link opened
   * in the messenger shows the ending instead of an empty frame.
   */
  const presentNothingToAsk = (
    options: SurveyStartOptions,
    payload: SurveyPayload,
    surveyId: string,
    format: SurveyRenderFormat,
    lang: string,
    known: Record<string, string>,
  ) => {
    sync?.stop();
    sync = null;
    const def = payload.definition;
    const endingId = def.endings[0]?.id;
    if ((format === "page" || options.inMessenger) && endingId) {
      set({
        ...initialState,
        phase: "ready",
        options,
        surveyId,
        payload,
        def,
        format,
        lang,
        channel: options.channel ?? detectChannel(format),
        path: [endingId],
        endingId,
        known,
      });
      return;
    }
    set({ ...initialState });
    send("survey-closed", {
      surveyId,
      version: payload.version,
      status: "not_started",
      reason: "nothing_to_ask",
    });
    Communicator.closeWidget();
  };

  const presentPayload = (
    options: SurveyStartOptions,
    payload: SurveyPayload,
    serverResume?: SurveyResumeData | null,
  ) => {
    const def = payload.definition;
    const format: SurveyRenderFormat =
      options.format ?? (def.format === "full" ? "full" : "card");
    const configStore = useConfigStore.getState();
    // The full code ("pt-br"): the survey's texts are keyed by it, and the
    // two-letter one would miss a regional translation (#185684).
    const lang = configStore.getCurrentLanguage() || def.defaultLanguage || "en";

    const choiceOrder: Record<string, string[]> = {};
    for (const block of def.blocks) {
      if (block.randomize && block.choices?.length) {
        choiceOrder[block.id] = shuffle(block.choices.map((c) => c.id));
      }
    }

    const stored = readSurveyState(
      payload.surveyId || options.surveyId,
      payload.version,
      !!options.resume,
    );
    // "Skip when known": fixed for this response from the session it starts with.
    const known = knownContactAnswers(def, useSessionStore.getState().session);
    const logicFor = (restoredAnswers: SurveyAnswers) => ({
      answers: restoredAnswers,
      contact: contactFromSession(useSessionStore.getState().session),
      fields: options.fields ?? {},
      url: hostPageUrl(options),
    });
    const passKnown: NextOf = (block, answer) => nextBlockId(def, block, answer, logicFor({}));
    // The first question to show: past the skipped ones at the start.
    const firstId = def.blocks[0]
      ? passSkipped(def, def.blocks[0].id, known, passKnown).id
      : def.endings[0]?.id ?? "";
    let path = [firstId];
    let cursor = 0;
    let answers: SurveyAnswers = {};
    let responseId = generateResponseId();
    let responseVersion = payload.version;
    let resumed = false;
    let alreadyCreated = false;
    if (stored && stored.path.length > 0) {
      const validPath = stored.path.filter((id) =>
        def.blocks.some((b) => b.id === id),
      );
      if (validPath.length > 0) {
        path = validPath;
        const last = stored.lastBlockId && validPath.indexOf(stored.lastBlockId);
        cursor = typeof last === "number" && last >= 0 ? last : validPath.length - 1;
        answers = stored.answers ?? {};
        responseId = stored.responseId;
        responseVersion = stored.responseVersion || stored.version;
        resumed = true;
        alreadyCreated = !!stored.started;
      }
    } else if (
      (options.resume && options.resumeData?.responseId) ||
      serverResume?.responseId
    ) {
      // Reminder on another device / native, or the server copy of a survey
      // closed halfway (no local state, e.g. the iOS web view has no storage):
      // continue the server response.
      const resumeData =
        options.resume && options.resumeData?.responseId
          ? options.resumeData
          : serverResume!;
      const restored = restoreFromResumeData(def, resumeData, (block, answer, restoredAnswers) =>
        passSkipped(def, nextBlockId(def, block, answer, logicFor(restoredAnswers)), known, (b, a) =>
          nextBlockId(def, b, a, logicFor(restoredAnswers)),
        ).id,
      );
      if (restored) {
        path = restored.path;
        cursor = restored.path.length - 1;
        answers = restored.answers;
        responseId = resumeData.responseId;
        responseVersion = resumeData.version || payload.version;
        resumed = true;
        alreadyCreated = true;
      }
    }

    if (Object.keys(known).length > 0) {
      // A restored path can hold questions now skipped as known (the server
      // copy has them, or the contact identified since): drop them and their
      // answers (the server fills those in from the session).
      const shown = resumed ? shownSteps(def, path, cursor, known, passKnown) : { path, cursor };
      answers = { ...answers };
      for (const block of def.blocks) {
        if (known[block.id] !== undefined) delete answers[block.key];
      }
      if (shown) {
        path = shown.path;
        cursor = shown.cursor;
      } else {
        path = [firstId];
        cursor = 0;
      }
    }

    const surveyId = payload.surveyId || options.surveyId;
    // Every question is skipped as known (a survey that only asks for contact details).
    if (def.blocks.length > 0 && (!path[cursor] || isEndingId(def, path[cursor]))) {
      presentNothingToAsk(options, payload, surveyId, format, lang, known);
      return;
    }

    const drafts: Record<string, SurveyAnswerValue | undefined> = {};
    for (const block of def.blocks) {
      if (answers[block.key] !== undefined) drafts[block.id] = answers[block.key];
    }

    sync?.stop();
    sync = null;

    // The first write of a response carries a reCAPTCHA token whenever one
    // can be had. Anonymous page/embed writes (no identified session, no
    // personal link) always need it, so reCAPTCHA is loaded there even if
    // the project has spam protection off.
    const session = useSessionStore.getState().session;
    const spamRequired =
      !!useConfigStore.getState().config?.spamProtection ||
      (!options.personalToken && (format === "page" || !session?.gleapId));
    if (spamRequired && !alreadyCreated) ensureRecaptchaLoaded();

    // An email link's answer is preset like a default answer and saved only once
    // the respondent confirms it: link scanners open every answer link in an email.
    const prefill = parsePrefill(def, options.prefill ?? options.fields?.__prefill);
    // The welcome screen opens a fresh response only: a resumed one, or one
    // whose first answer came with the link, goes straight to the question.
    const welcome =
      !!def.welcome?.enabled &&
      def.blocks.length > 0 &&
      !resumed &&
      Object.keys(answers).length === 0 &&
      !prefill;
    // Step analytics: before the state turns ready (the subscription below reports the first step).
    surveyAnalytics.begin({
      surveyId,
      version: responseVersion,
      responseId,
      channel: options.channel ?? detectChannel(format),
      language: lang,
      definition: def,
      pageUrl: hostPageUrl(options),
      referrer: typeof options.source?.referrer === "string" ? options.source.referrer : format === "page" ? document.referrer : undefined,
      sdkVersion: (options as any).sdkVersion,
      forward: payload.analytics?.forward === true,
      surveyName: payload.analytics?.name,
      resumed,
    });
    set({
      ...initialState,
      phase: "ready",
      options,
      surveyId,
      payload,
      def: prefill
        ? {
            ...def,
            blocks: def.blocks.map((block) =>
              block.id === prefill.block.id ? { ...block, defaultValue: prefill.value } : block,
            ),
          }
        : def,
      format,
      lang,
      channel: options.channel ?? detectChannel(format),
      responseId,
      responseVersion,
      path,
      cursor,
      answers,
      drafts,
      choiceOrder,
      resumed,
      welcome,
      known,
      answeredOnce: resumed && Object.keys(answers).length > 0,
      saveState: resumed ? "saved" : "idle",
    });
    if (welcome) surveyAnalytics.welcomeViewed();

    sync = new SurveyResponseSync(
      surveyId,
      responseId,
      (saveState) => set({ saveState }),
      {
        spamTokenProvider: () => getRecaptchaToken("submitsurvey", spamRequired),
        alreadyCreated,
      },
    );
    bindLifecycle();

    postSurveyImpression(surveyId, payload.version, get().channel);

    send("survey-shown", {
      surveyId,
      version: responseVersion,
      format,
      responseId,
      resumed,
      ...surveyAnalytics.hostFields(),
    });
    Communicator.notifyEvent("flow-started", {
      id: surveyId,
      surveyId,
      type: "SURVEY",
      version: payload.version,
      format,
    });
  };

  const finish = (endingId: string) => {
    const s = get();
    set({ endingId, direction: "forward" });
    persist(true);
    const data = {
      surveyId: s.surveyId,
      version: s.responseVersion || s.payload?.version,
      responseId: s.responseId,
      endingId,
      // Host callbacks get the skipped-as-known values too (the legacy form reported its hidden email).
      answers: { ...get().knownAnswers(), ...get().answers },
    };
    surveyAnalytics.completed(endingId);
    send("survey-completed", { ...data, ...surveyAnalytics.hostFields() });
    // Existing SDK callbacks (Gleap.on('outbound-sent'), Gleap.on('feedback-sent'))
    // keep firing for v2 surveys: every JS SDK version relays notify-event to
    // its event manager. Native SDKs that announce `surveyCallbacks` turn
    // outbound-sent into feedbackSent + outboundSent; they ignore feedback-sent.
    const outbound = {
      id: s.surveyId,
      type: "SURVEY",
      version: data.version,
      format: s.format,
    };
    // A migrated survey reports its answers under the legacy field names.
    const formData = legacyFormDataOf(data.answers, s.payload?.legacyKeyMap);
    Communicator.notifyEvent("outbound-sent", {
      outboundId: s.surveyId,
      outbound,
      formData,
      surveyId: s.surveyId,
      responseId: s.responseId,
      endingId,
      status: "completed",
    });
    Communicator.notifyEvent("feedback-sent", formData);

    const config = useConfigStore.getState();
    if (config.isApp && !config.hostSurveyCallbacks) {
      legacyAckUntil = Date.now() + 60 * 1000;
      send("send-feedback", {
        formData,
        action: {
          ...outbound,
          responseId: s.responseId,
          endingId,
          feedbackType: "SURVEY",
          excludeData: LEGACY_SURVEY_EXCLUDE_DATA,
        },
        outboundId: s.surveyId,
        spamToken: `${SURVEY_RESPONSE_ACK_TOKEN_PREFIX}${s.responseId}`,
      });
    }
  };

  return {
    ...initialState,

    load: async (options) => {
      const token = ++loadToken;
      sync?.stop();
      sync = null;
      set({
        ...initialState,
        phase: "loading",
        options,
        surveyId: options.surveyId,
        format: options.format ?? "card",
      });
      try {
        await waitForSession();
        const lang = useConfigStore.getState().getCurrentLanguage() || "en";
        // No local resume state (iOS opens every survey in a fresh web view
        // without storage): ask the server for a response this session left
        // halfway, in parallel with the definition and capped at 1.5 s. Only
        // where local state can't exist (native shells, blocked storage): on
        // the web every show would otherwise cost a request + a Mongo read.
        const session = useSessionStore.getState().session;
        const askServer =
          !!session?.gleapId &&
          (useConfigStore.getState().isApp || !surveyStorageAvailable()) &&
          options.format !== "page" &&
          !options.personalToken &&
          !(options.resume && options.resumeData?.responseId) &&
          !hasStoredSurveyState(options.surveyId, !!options.resume);
        const serverResume = askServer
          ? fetchCurrentSurveyResponse(options.surveyId, 1500)
          : Promise.resolve(null);
        const result = await fetchSurveyDefinition(options.surveyId, lang);
        if (token !== loadToken) return "v2";
        const data = result.data;
        if (
          result.status !== 200 ||
          !data ||
          !data.definition ||
          !Array.isArray(data.definition.blocks) ||
          !Array.isArray(data.definition.endings) ||
          data.definition.endings.length === 0
        ) {
          set({ phase: "idle" });
          return "legacy";
        }
        const resumeData = await serverResume;
        if (token !== loadToken) return "v2";
        presentPayload(
          options,
          {
            ...data,
            surveyId: data.surveyId || options.surveyId,
          },
          resumeData,
        );
        return "v2";
      } catch (exp) {
        if (token !== loadToken) return "v2";
        set({ phase: "idle" });
        return "legacy";
      }
    },

    present: (options, payload) => {
      ++loadToken;
      presentPayload(options, payload);
    },

    startSurvey: () => {
      const s = get();
      if (!s.welcome || s.closed || s.phase !== "ready") return;
      surveyAnalytics.welcomeStarted();
      set({ welcome: false, direction: "forward" });
    },

    setDraft: (blockId, value) => {
      set((s) => ({
        drafts: { ...s.drafts, [blockId]: value },
        errors: s.errors[blockId] ? { ...s.errors, [blockId]: undefined } : s.errors,
      }));
    },

    submitCurrent: () => {
      const block = get().currentBlock();
      if (!block) return false;
      const draft = get().drafts[block.id];
      const error = validateSurveyAnswer(block, draft);
      if (error) {
        set((s) => ({ errors: { ...s.errors, [block.id]: error } }));
        return false;
      }
      get().submit(block.id, toAnswer(block, draft));
      return true;
    },

    skipCurrent: () => {
      const block = get().currentBlock();
      if (!block || block.required) return;
      get().setDraft(block.id, undefined);
      get().submit(block.id, undefined);
    },

    submit: (blockId, value) => {
      const s = get();
      const def = s.def;
      if (!def || s.endingId || s.closed || s.welcome) return;
      // Ignore late auto-advance timers for a block that's no longer shown.
      if (s.path[s.cursor] !== blockId) return;
      const block = def.blocks.find((b) => b.id === blockId);
      if (!block) return;

      const keep = s.path.slice(0, s.cursor + 1);
      const dropped = s.path.slice(s.cursor + 1);
      const answers: SurveyAnswers = { ...s.answers };
      for (const id of dropped) {
        const droppedBlock = def.blocks.find((b) => b.id === id);
        if (droppedBlock && !keep.includes(id)) delete answers[droppedBlock.key];
      }
      if (block.type !== "statement") {
        if (value === undefined || value === null) delete answers[block.key];
        else answers[block.key] = value;
      }

      set({ answers });
      // A jump to a question skipped as known continues where that question goes.
      const next = passSkipped(
        def,
        nextBlockId(def, block, value, { ...logicContext(), answers }),
        s.known,
        nextOf(),
      ).id;

      const isAnswer = block.type !== "statement";
      surveyAnalytics.answered(block, value);
      if (isAnswer) {
        send("survey-answered", {
          surveyId: s.surveyId,
          version: s.responseVersion || s.payload?.version,
          responseId: s.responseId,
          blockId: block.id,
          key: block.key,
          value: value === undefined ? null : value,
          stepIndex: def.blocks.indexOf(block),
          ...surveyAnalytics.hostFields(),
        });
      }

      if (!next || isEndingId(def, next)) {
        set({ path: keep, answeredOnce: true });
        finish(next || def.endings[0]?.id);
        return;
      }

      // A response is created with the first ANSWER: moving past a statement
      // before anything was answered writes nothing.
      const hasAnswers = Object.keys(answers).length > 0;
      set({
        path: [...keep, next],
        cursor: keep.length,
        direction: "forward",
        answeredOnce: s.answeredOnce || hasAnswers,
      });
      if (hasAnswers || s.answeredOnce) persist(false);
    },

    back: () => {
      const s = get();
      if (s.endingId || s.welcome || s.cursor <= 0) return;
      surveyAnalytics.back(s.path[s.cursor]);
      set({ cursor: s.cursor - 1, direction: "back" });
    },

    forward: () => {
      const s = get();
      if (s.endingId) return;
      if (s.welcome) {
        get().startSurvey();
        return;
      }
      // Moving down re-confirms the shown answer (so a changed answer re-routes).
      get().submitCurrent();
    },

    close: () => {
      const s = get();
      if (s.closed) return;
      try {
        sync?.flushWithKeepalive();
      } catch (exp) {}
      set({ closed: true });
      surveyAnalytics.closed(
        s.endingId || s.welcome ? undefined : s.path[s.cursor],
        s.endingId ? "completed" : Object.keys(s.answers).length > 0 ? "in_progress" : "not_started",
      );
      send("survey-closed", {
        surveyId: s.surveyId,
        version: s.responseVersion || s.payload?.version,
        responseId: s.responseId,
        status: s.endingId
          ? "completed"
          : Object.keys(s.answers).length > 0
          ? "in_progress"
          : "not_started",
      });
      // Opened from a link inside the messenger: back to the page the link was on.
      if (s.options?.inMessenger) {
        get().reset();
        const router = useRouterStore.getState();
        if (router.currentPage === "survey-inline") router.popPage();
        return;
      }
      Communicator.closeWidget();
    },

    reset: () => {
      try {
        if (sync?.hasUnsaved) sync.flushWithKeepalive();
      } catch (exp) {}
      sync?.stop();
      sync = null;
      surveyAnalytics.end();
      ++loadToken;
      set({ ...initialState });
    },

    currentBlock: () => {
      const s = get();
      if (!s.def || s.endingId || s.welcome) return undefined;
      const id = s.path[s.cursor];
      return s.def.blocks.find((b) => b.id === id);
    },

    currentEnding: () => {
      const s = get();
      if (!s.def) return undefined;
      const id = s.endingId ?? s.path[s.cursor];
      return s.def.endings.find((e) => e.id === id);
    },

    progress: () => {
      const s = get();
      if (!s.def) return 0;
      if (s.endingId) return 1;
      const answered = s.path.slice(0, s.cursor).length;
      return progressEstimate(s.def, s.path[s.cursor], answered, (id) => s.known[id] !== undefined);
    },

    questionNumber: (blockId) => {
      const { def, path } = get();
      if (!def) return 0;
      // Numbered by position on the respondent's path, so blocks skipped by
      // logic leave no gaps (1, 2, 3, 4 — never 1, 2, 3, 7). Statements
      // aren't numbered (Typeform style).
      const isQuestion = (id: string) =>
        def.blocks.some((b) => b.id === id && b.type !== "statement");
      const index = path.indexOf(blockId);
      if (index >= 0) {
        return path.slice(0, index + 1).filter(isQuestion).length;
      }
      const { known } = get();
      const questions = def.blocks.filter((b) => b.type !== "statement" && known[b.id] === undefined);
      return questions.findIndex((b) => b.id === blockId) + 1;
    },

    knownAnswers: () => {
      const { def, known } = get();
      const answers: SurveyAnswers = {};
      for (const block of def?.blocks ?? []) {
        if (known[block.id] !== undefined) answers[block.key] = known[block.id];
      }
      return answers;
    },
  };
});

// step_viewed: whichever block the cursor lands on (forward, back, resume), once per step per response.
useSurveyV2Store.subscribe((state) => {
  if (state.phase === "ready" && !state.endingId && !state.closed && !state.welcome) {
    surveyAnalytics.stepShown(state.path[state.cursor]);
  }
});
